import { useEffect, useState } from 'react';
import type { DueRow } from '../lib/permits';
import { permitsApi, whenLabel } from '../lib/permits';
import { formatDate } from '../lib/money';

/**
 * "Expiring soon" — the permits, vehicle papers and plant services due within
 * their windows. A service says "overdue", not "expired", and its button
 * "Serviced ›" (31 October 2026).
 *
 * Two shapes for the same list (decided on the owner's mockup, 23 September
 * 2026): a card at the top of Approvals for the owner, with a way to the
 * Permits tab where he renews; and a strip above the day for the office, who
 * sees the same reminders but cannot change anything. Read live on mount;
 * with no signal it draws nothing rather than something stale.
 *
 * Red: expired, or within 7 days. Amber: inside the reminder window.
 */

interface Props {
  variant: 'card' | 'strip';
  /** Owner only: opens the Permits tab. */
  onGo?: (() => void) | undefined;
}

const TONE: Record<DueRow['status'], { className: string }> = {
  expired: { className: 'b-no' },
  red: { className: 'b-no' },
  amber: { className: 'b-warn' },
};

export default function ExpiringSoon({ variant, onGo }: Props) {
  const [due, setDue] = useState<DueRow[] | null>(null);
  const [expired, setExpired] = useState(0);

  useEffect(() => {
    let live = true;
    permitsApi
      .reminders()
      .then((result) => {
        if (!live) return;
        setDue(result.due);
        setExpired(result.expired);
      })
      .catch(() => {
        /* no signal, or nothing to say: the card does not draw */
      });
    return () => { live = false; };
  }, []);

  if (!due || due.length === 0) return null;

  if (variant === 'strip') {
    const worst = due[0]!;
    const rest = due.slice(1);
    return (
      <div className={`banner ${TONE[worst.status].className}`} style={{ display: 'block' }}>
        <div style={{ fontWeight: 600 }}>
          {worst.title} · {whenLabel(worst.daysLeft, worst.kind)}
        </div>
        <div style={{ fontSize: 13, marginTop: 2 }}>
          {worst.kind === 'service'
            ? worst.status === 'expired'
              ? `Was due ${formatDate(worst.expiresOn)}. Tell the owner.`
              : `Due ${formatDate(worst.expiresOn)}.`
            : worst.status === 'expired'
              ? `Ran out ${formatDate(worst.expiresOn)}. Tell the owner.`
              : `Expires ${formatDate(worst.expiresOn)}.`}
          {rest.length > 0 && ` ${rest.length} more due: ${rest.map((r) => `${r.title} · ${whenLabel(r.daysLeft, r.kind)}`).join(' — ')}`}
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="row" style={{ paddingTop: 0 }}>
        <div>
          <h3>Expiring soon</h3>
          <div className="sub">
            Permits, vehicle papers and plant services due. The office sees this too; only you can
            mark one renewed.
          </div>
        </div>
      </div>

      {due.map((row) => (
        <div
          className={`banner ${TONE[row.status].className}`}
          key={`${row.kind}:${row.id}:${row.paper ?? ''}`}
          style={{ justifyContent: 'space-between', margin: '8px 0 0' }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>
              {row.title} · {whenLabel(row.daysLeft, row.kind)}
            </div>
            <div style={{ fontSize: 12, marginTop: 2 }}>
              {row.detail && `${row.detail} · `}
              {row.kind === 'service'
                ? row.status === 'expired' ? 'was due ' : 'due '
                : row.status === 'expired' ? 'ran out ' : 'expires '}
              {formatDate(row.expiresOn)}
            </div>
          </div>
          {onGo && (
            <button type="button" className="btn sm" style={{ minWidth: 0 }} onClick={onGo}>
              {row.kind === 'service' ? 'Serviced ›' : 'Renew ›'}
            </button>
          )}
        </div>
      ))}

      <div className="sub" style={{ marginTop: 10 }}>
        {due.length} due{expired > 0 && ` · ${expired} already overdue`}
      </div>
    </div>
  );
}
